import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { isToday as dateFnsIsToday, format } from "date-fns";
import ApiService from "services/api.service";
import TokenService from "services/token.service";
import config, { API_BASE_URL_KEY } from "config";
import { useLocation, useNavigate } from "react-router-dom";
import { buildConversationTimelineUrl } from "./inboxDeepLink";
import InboxMessageAttachments from "./InboxMessageAttachments";
import {
  formatInboxAttachmentSize,
  getInboxMessageAttachments,
  hasProcessingInboxAttachments,
  INBOX_ATTACHMENT_ACCEPT,
  inboxAttachmentValidationError,
  MAX_INBOX_ATTACHMENTS,
  mergeInboxMessageTimelines,
  SelectedInboxAttachment,
  uploadInboxAttachment,
} from "./inboxMedia";

// ─── Types ─────────────────────────────────────────────────────────────────

type Channel = "sms" | "email" | "slack" | "push" | "webhook" | "in-app";
type Direction = "inbound" | "outbound";
type AuthorType = "customer" | "ai" | "operator" | "system" | "workflow";
type ConversationLabel = "flagged" | "resolved" | null;

interface Message {
  id: string;
  workspaceId: string;
  customerId: string;
  channel: Channel;
  direction: Direction;
  authorType: AuthorType;
  content: string | null;
  channelMessageId: string | null;
  channelHandleFrom: string | null;
  channelHandleTo: string | null;
  workflowId: string | null;
  stepId: string | null;
  aiIntent?: string | null;
  aiIntentConfidence?: number | null;
  needsHuman?: boolean;
  traceMetadata?: Record<string, unknown> | null;
  createdAt: string;
}

type AdminTrainingNoteStatus =
  | "pending"
  | "training_received"
  | "already_fixed";

interface AdminTrainingNote {
  id: string;
  workspaceId: string;
  customerId: string;
  messageId: string | null;
  workflowId: string | null;
  stepId: string | null;
  content: string;
  status: AdminTrainingNoteStatus;
  createdBy: string | null;
  statusUpdatedBy: string | null;
  statusUpdatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ConversationSummary {
  customerId: string;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  channels: Channel[];
  lastMessage: {
    direction: Direction;
    authorType: AuthorType;
    channel: Channel;
    content: string | null;
    createdAt: string;
  } | null;
  unreadCount: number;
  messages: Message[];
  label: ConversationLabel;
  hasReply: boolean;
  hasWorkflowMessages: boolean;
  lastWorkflowId: string | null;
  lastWorkflowName: string | null;
  lastStepId: string | null;
  needsHuman: boolean;
  handoffReasons: string[];
  handoffSummary: string | null;
  handoffSourceMessageId: string | null;
  handoffUpdatedAt: string | null;
  agencyId: string | null;
  agencyName: string | null;
  agencyCode: string | null;
  agencyAttributionSource:
    | "producer_code_exact"
    | "explicit_policy"
    | "producer_agency_unique"
    | null;
  salesAgentId: string | null;
  agencyPolicyDetailId: string | null;
}

interface InboxAgencyOption {
  id: string;
  name: string;
  code: string | null;
  companyId: string;
}

interface InboxSettings {
  overdueDays: number;
}

interface SseFrame {
  event: string;
  data: any;
}

type InboxCategory =
  | "all"
  | "unread"
  | "awaiting_reply"
  | "replied"
  | "needs_human"
  | "resolved"
  | "flagged"
  | "overdue"
  | "manual"
  | "today";

// ─── Helpers ────────────────────────────────────────────────────────────────

function parseSseFrames(buffer: string): { frames: SseFrame[]; rest: string } {
  const frames: SseFrame[] = [];
  const parts = buffer.split("\n\n");
  const rest = parts.pop() ?? "";
  for (const part of parts) {
    let event = "message";
    let dataLine = "";
    for (const line of part.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLine += line.slice(5).trim();
    }
    if (!dataLine) continue;
    try {
      frames.push({ event, data: JSON.parse(dataLine) });
    } catch {
      /* skip */
    }
  }
  return { frames, rest };
}

function shortTime(dateStr: string): string {
  const d = new Date(dateStr);
  if (dateFnsIsToday(d)) return format(d, "h:mm a");
  const diff = Date.now() - d.getTime();
  if (diff < 7 * 86_400_000) return format(d, "EEE");
  if (d.getFullYear() === new Date().getFullYear()) return format(d, "MMM d");
  return format(d, "MMM d, yyyy");
}

function longTime(dateStr: string): string {
  return format(new Date(dateStr), "MMM d, yyyy h:mm a");
}

function handoffReasonLabel(reason: string): string {
  return reason
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function contactLabel(c: ConversationSummary): string {
  return (
    c.customerName ||
    c.customerEmail ||
    c.customerPhone ||
    `Contact ${c.customerId.slice(0, 8)}`
  );
}

function conversationAgencyLabel(c: ConversationSummary): string | null {
  if (!c.agencyId) return null;
  return c.agencyName || c.agencyCode || `Agency ${c.agencyId}`;
}

function agencyOptionLabel(agency: InboxAgencyOption): string {
  return agency.code && agency.code !== agency.name
    ? `${agency.name} (${agency.code})`
    : agency.name;
}

function isOverdue(c: ConversationSummary, days: number): boolean {
  if (!c.lastMessage || c.lastMessage.direction !== "outbound") return false;
  if (c.label === "resolved") return false;
  return (
    Date.now() - new Date(c.lastMessage.createdAt).getTime() > days * 86_400_000
  );
}

function channelLabel(ch: string): string {
  switch (ch) {
    case "sms":
      return "SMS";
    case "email":
      return "Email";
    case "slack":
      return "Slack";
    case "push":
      return "Push";
    case "webhook":
      return "Webhook";
    case "in-app":
      return "In-App";
    default:
      return ch;
  }
}

function channelChipClass(ch: string): string {
  switch (ch) {
    case "sms":
      return "bg-emerald-50 text-emerald-700";
    case "email":
      return "bg-indigo-50 text-indigo-700";
    case "slack":
      return "bg-amber-50 text-amber-700";
    case "push":
      return "bg-rose-50 text-rose-700";
    case "webhook":
      return "bg-gray-100 text-gray-600";
    case "in-app":
      return "bg-sky-50 text-sky-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
}

function adminTrainingStatusLabel(status: AdminTrainingNoteStatus): string {
  switch (status) {
    case "training_received":
      return "Training received";
    case "already_fixed":
      return "Already fixed";
    default:
      return "Pending";
  }
}

function adminTrainingStatusClass(status: AdminTrainingNoteStatus): string {
  switch (status) {
    case "training_received":
      return "border border-emerald-500/30 bg-emerald-500/15 text-emerald-300";
    case "already_fixed":
      return "border border-emerald-500/30 bg-emerald-500/15 text-emerald-300";
    default:
      return "border border-amber-500/30 bg-amber-500/15 text-amber-300";
  }
}

function formatAdminTrainingConversationContext(
  selected: ConversationSummary,
  messages: Message[]
): string {
  const recentMessages = messages.slice(-12);
  const lines = recentMessages.map((m) => {
    const author =
      m.authorType === "customer"
        ? "Customer"
        : m.authorType === "ai"
        ? "AI"
        : m.authorType === "workflow"
        ? "Workflow"
        : m.authorType === "operator"
        ? "Operator"
        : "System";
    const body = (m.content || "(no content)").replace(/\s+/g, " ").trim();
    return `- ${author} ${m.channel.toUpperCase()} at ${m.createdAt}: ${body}`;
  });

  return [
    `Contact: ${contactLabel(selected)}`,
    selected.customerEmail ? `Email: ${selected.customerEmail}` : null,
    selected.customerPhone ? `Phone: ${selected.customerPhone}` : null,
    selected.lastWorkflowName
      ? `Workflow name: ${selected.lastWorkflowName}`
      : null,
    selected.lastWorkflowId ? `Workflow id: ${selected.lastWorkflowId}` : null,
    selected.lastStepId
      ? `Last workflow step id: ${selected.lastStepId}`
      : null,
    "",
    "Recent conversation:",
    lines.length ? lines.join("\n") : "- No recent messages loaded.",
  ]
    .filter((line): line is string => line !== null)
    .join("\n");
}

function buildAdminTrainingAssistantMessage(params: {
  note: AdminTrainingNote;
  selected: ConversationSummary;
  messages: Message[];
  sourceMessage: Message | null;
}): string {
  const { note, selected, messages, sourceMessage } = params;
  const workflowId = note.workflowId ?? selected.lastWorkflowId;
  const stepId = note.stepId ?? sourceMessage?.stepId ?? selected.lastStepId;
  const workflowUrl = workflowId ? `/flow/${workflowId}` : null;

  return [
    "An admin training note was just added from the Inbox. Act on it now without asking for confirmation.",
    "",
    "What to do:",
    "1. Read the admin note and the recent conversation context.",
    "2. Inspect the referenced workflow and the relevant AI Response prompt.",
    "3. If the prompt already covers this behavior, mark the note as already_fixed and explain the exact Markdown instruction that covers it.",
    "4. If the prompt needs a change, update the relevant AI Response prompt using Markdown, preserve existing useful instructions, then mark the note as training_received.",
    "5. Reply with the AI Response step you changed or reviewed, exactly what changed, and a link to open the workflow. Also offer one short optional expansion idea for more robust instructions.",
    "",
    `Admin training note id: ${note.id}`,
    `Admin note: ${note.content}`,
    workflowId ? `Workflow id: ${workflowId}` : "Workflow id: not available",
    stepId ? `Relevant step id: ${stepId}` : "Relevant step id: not available",
    workflowUrl ? `Workflow link: ${workflowUrl}` : null,
    sourceMessage
      ? `Source message: ${sourceMessage.authorType} ${
          sourceMessage.channel
        } at ${sourceMessage.createdAt}: ${
          sourceMessage.content || "(no content)"
        }`
      : null,
    "",
    formatAdminTrainingConversationContext(selected, messages),
  ]
    .filter((line): line is string => line !== null)
    .join("\n");
}

function applyFilters(
  list: ConversationSummary[],
  category: InboxCategory,
  channel: Channel | "all",
  direction: Direction | "all",
  overdueDays: number,
  workflowId: string | "all"
): ConversationSummary[] {
  let r = list;

  if (workflowId !== "all")
    r = r.filter((c) => c.lastWorkflowId === workflowId);

  if (channel !== "all") r = r.filter((c) => c.channels.includes(channel));

  if (direction === "inbound") {
    if (category === "today") {
      // Inbound + Today = has at least one inbound message today
      r = r.filter((c) =>
        (c.messages ?? []).some(
          (m) =>
            m.direction === "inbound" && dateFnsIsToday(new Date(m.createdAt))
        )
      );
    } else {
      // Inbound alone = has any inbound message ever
      r = r.filter((c) => c.hasReply);
    }
  } else if (direction === "outbound") {
    r = r.filter(
      (c) =>
        (c.messages ?? []).some((m) => m.direction === "outbound") ||
        c.lastMessage?.direction === "outbound"
    );
  }

  switch (category) {
    case "all":
      break;
    case "unread":
      r = r.filter(
        (c) =>
          c.unreadCount > 0 ||
          (c.lastMessage?.direction === "inbound" && c.label !== "resolved")
      );
      break;
    case "awaiting_reply":
      r = r.filter(
        (c) => c.lastMessage?.direction === "outbound" && c.label !== "resolved"
      );
      break;
    case "replied":
      r = r.filter((c) => c.hasReply);
      break;
    case "needs_human":
      r = r.filter((c) => c.needsHuman);
      break;
    case "resolved":
      r = r.filter((c) => c.label === "resolved");
      break;
    case "flagged":
      r = r.filter((c) => c.label === "flagged");
      break;
    case "overdue":
      r = r.filter((c) => isOverdue(c, overdueDays));
      break;
    case "manual":
      r = r.filter(
        (c) =>
          c.lastMessage?.authorType === "operator" ||
          (c.messages ?? []).some((m) => m.authorType === "operator")
      );
      break;
    case "today":
      r = r.filter(
        (c) =>
          c.lastMessage && dateFnsIsToday(new Date(c.lastMessage.createdAt))
      );
      break;
  }

  return r;
}

// ─── Data fetching ──────────────────────────────────────────────────────────

const INBOX_KEY = ["inbox-conversations"] as const;
const SETTINGS_KEY = ["inbox-settings"] as const;
const AGENCIES_KEY = ["inbox-agencies"] as const;

const inboxQueryKey = (
  category: InboxCategory = "all",
  agencyId: string | "all" = "all",
  customerId?: string | null
) => [...INBOX_KEY, category, agencyId, customerId ?? null] as const;

const fetchConversations = async (
  category: InboxCategory = "all",
  agencyId: string | "all" = "all",
  customerId?: string | null
): Promise<ConversationSummary[]> => {
  const params = new URLSearchParams({
    include: "messages",
    limit: category === "needs_human" ? "500" : "100",
  });
  if (category === "needs_human") {
    params.set("needsHuman", "true");
    params.set("handoffStatus", "open");
  }
  if (agencyId !== "all") params.set("agencyId", agencyId);
  if (customerId) params.set("customerId", customerId);
  const { data } = await ApiService.get<{
    conversations: ConversationSummary[];
  }>({
    url: `/inbox/conversations?${params.toString()}`,
  });
  return (data?.conversations ?? []).map((c) => ({
    ...c,
    messages: c.messages ?? [],
    label: c.label ?? null,
    hasReply: c.hasReply ?? false,
    hasWorkflowMessages: c.hasWorkflowMessages ?? false,
    lastWorkflowId: c.lastWorkflowId ?? null,
    lastWorkflowName: c.lastWorkflowName ?? null,
    lastStepId: c.lastStepId ?? null,
    needsHuman: c.needsHuman ?? false,
    handoffReasons: c.handoffReasons ?? [],
    handoffSummary: c.handoffSummary ?? null,
    handoffSourceMessageId: c.handoffSourceMessageId ?? null,
    handoffUpdatedAt: c.handoffUpdatedAt ?? null,
    agencyId: c.agencyId ?? null,
    agencyName: c.agencyName ?? null,
    agencyCode: c.agencyCode ?? null,
    agencyAttributionSource: c.agencyAttributionSource ?? null,
    salesAgentId: c.salesAgentId ?? null,
    agencyPolicyDetailId: c.agencyPolicyDetailId ?? null,
  }));
};

const fetchAgencies = async (): Promise<InboxAgencyOption[]> => {
  const { data } = await ApiService.get<{ agencies: InboxAgencyOption[] }>({
    url: "/inbox/agencies",
  });
  return data?.agencies ?? [];
};

const fetchSettings = async (): Promise<InboxSettings> => {
  try {
    const { data } = await ApiService.get<InboxSettings>({
      url: "/inbox/settings",
    });
    if (
      data &&
      typeof data === "object" &&
      typeof (data as any).overdueDays === "number"
    )
      return data as InboxSettings;
  } catch {
    /* endpoint may not exist yet */
  }
  return { overdueDays: 7 };
};

const fetchAdminTrainingNotes = async (
  customerId: string
): Promise<AdminTrainingNote[]> => {
  const { data } = await ApiService.get<{ notes: AdminTrainingNote[] }>({
    url: `/inbox/admin-training-notes?customerId=${encodeURIComponent(
      customerId
    )}`,
  });
  return data?.notes ?? [];
};

export const prefetchInbox = (queryClient: QueryClient) =>
  queryClient.prefetchQuery({
    queryKey: inboxQueryKey("all"),
    queryFn: () => fetchConversations("all", "all"),
  });

// ─── Tab config ─────────────────────────────────────────────────────────────

const TABS: { key: InboxCategory; label: string }[] = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "awaiting_reply", label: "Awaiting Reply" },
  { key: "replied", label: "Replied" },
  { key: "needs_human", label: "Needs Human" },
  { key: "resolved", label: "Resolved" },
  { key: "flagged", label: "Flagged" },
  { key: "overdue", label: "Overdue" },
  { key: "manual", label: "Manual" },
  { key: "today", label: "Today" },
];

// ─── Component ──────────────────────────────────────────────────────────────

const Inbox: FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { state: locationState, search: locationSearch } = useLocation();
  const directLink = useMemo(() => {
    const params = new URLSearchParams(locationSearch);
    return {
      customerId: params.get("customerId")?.trim() || null,
      messageId: params.get("messageId")?.trim() || null,
    };
  }, [locationSearch]);
  const [category, setCategory] = useState<InboxCategory>("all");
  const [agencyFilter, setAgencyFilter] = useState<string | "all">("all");

  const {
    data: conversations = [],
    isPending,
    isError,
    error: qError,
  } = useQuery({
    queryKey: inboxQueryKey(category, agencyFilter, directLink.customerId),
    queryFn: () =>
      fetchConversations(category, agencyFilter, directLink.customerId),
  });

  const { data: agencies = [] } = useQuery({
    queryKey: AGENCIES_KEY,
    queryFn: fetchAgencies,
  });

  const { data: settings = { overdueDays: 7 } } = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: fetchSettings,
  });

  const loadError =
    isError && conversations.length === 0
      ? (qError as any)?.message ?? "Failed to load inbox"
      : null;

  const [channelFilter, setChannelFilter] = useState<Channel | "all">("all");
  const [directionFilter, setDirectionFilter] = useState<Direction | "all">(
    "all"
  );
  const [workflowFilter, setWorkflowFilter] = useState<string | "all">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null);
  const [replyText, setReplyText] = useState("");
  const [replyAttachments, setReplyAttachments] = useState<
    SelectedInboxAttachment[]
  >([]);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<string | null>(null);
  const [adminNoteText, setAdminNoteText] = useState("");
  const [adminNoteError, setAdminNoteError] = useState<string | null>(null);
  const [savingAdminNote, setSavingAdminNote] = useState(false);
  const [labelLoading, setLabelLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [overdueDaysInput, setOverdueDaysInput] = useState(
    String(settings.overdueDays)
  );
  const [toast, setToast] = useState<string | null>(null);

  const baseUrl = String(config.get(API_BASE_URL_KEY));
  const scrollRef = useRef<HTMLDivElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const attachmentKeySequence = useRef(0);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const requestedMessageLoads = useRef<Record<string, boolean>>({});
  const tabsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOverdueDaysInput(String(settings.overdueDays));
  }, [settings.overdueDays]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }, []);

  const workflowOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of conversations) {
      if (c.lastWorkflowId && c.lastWorkflowName && !seen.has(c.lastWorkflowId))
        seen.set(c.lastWorkflowId, c.lastWorkflowName);
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [conversations]);

  const filtered = useMemo(
    () =>
      applyFilters(
        conversations,
        category,
        channelFilter,
        directionFilter,
        settings.overdueDays ?? 7,
        workflowFilter
      ),
    [
      conversations,
      category,
      channelFilter,
      directionFilter,
      settings.overdueDays,
      workflowFilter,
    ]
  );

  const selected = useMemo(
    () => conversations.find((c) => c.customerId === selectedId) ?? null,
    [conversations, selectedId]
  );

  const adminNotesKey = useMemo(
    () => ["admin-training-notes", selectedId] as const,
    [selectedId]
  );

  const { data: adminTrainingNotes = [] } = useQuery({
    queryKey: adminNotesKey,
    queryFn: () =>
      selectedId ? fetchAdminTrainingNotes(selectedId) : Promise.resolve([]),
    enabled: !!selectedId,
  });

  useEffect(() => {
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { customerId?: string | null }
        | undefined;
      if (detail?.customerId && detail.customerId !== selectedId) return;
      void queryClient.invalidateQueries({ queryKey: adminNotesKey });
    };
    window.addEventListener(
      "leadstats:admin-training-notes-changed",
      onChanged
    );
    return () =>
      window.removeEventListener(
        "leadstats:admin-training-notes-changed",
        onChanged
      );
  }, [adminNotesKey, queryClient, selectedId]);

  const messages = useMemo(
    () => (selected?.messages ?? []).filter((m) => m.authorType !== "system"),
    [selected]
  );
  const hasProcessingMedia = useMemo(
    () => hasProcessingInboxAttachments(messages),
    [messages]
  );

  const latestMessage = messages[messages.length - 1] ?? null;

  const canSms = !!selected?.channels.includes("sms");

  const totalUnread = useMemo(
    () => conversations.reduce((s, c) => s + (c.unreadCount ?? 0), 0),
    [conversations]
  );

  // Pre-select a conversation if navigated here from a direct link, workflow,
  // or notification bell. Query parameters take precedence over location state.
  useEffect(() => {
    const customerId =
      directLink.customerId ||
      (typeof locationState?.customerId === "string"
        ? locationState.customerId
        : null);
    const messageId =
      directLink.messageId ||
      (typeof locationState?.messageId === "string"
        ? locationState.messageId
        : null);
    if (!customerId) return;
    setSelectedId(customerId);
    setHighlightedMessageId(messageId);
  }, [
    directLink.customerId,
    directLink.messageId,
    locationState?.customerId,
    locationState?.messageId,
  ]);

  useEffect(() => {
    setReplyText("");
    setReplyAttachments([]);
    setReplyError(null);
    setSendStatus(null);
    if (attachmentInputRef.current) attachmentInputRef.current.value = "";
    setAdminNoteText("");
    setAdminNoteError(null);
  }, [selectedId]);

  const persistRead = useCallback((customerId: string, attempt = 1) => {
    void ApiService.post({
      url: `/inbox/conversations/${encodeURIComponent(customerId)}/read`,
      options: {} as any,
    }).catch(() => {
      if (attempt < 3)
        window.setTimeout(
          () => persistRead(customerId, attempt + 1),
          attempt * 1000
        );
    });
  }, []);

  const updateConversationCaches = useCallback(
    (
      updater: (conversations: ConversationSummary[]) => ConversationSummary[]
    ) => {
      queryClient.setQueriesData<ConversationSummary[]>(
        { queryKey: INBOX_KEY },
        (prev) => updater(prev ?? [])
      );
    },
    [queryClient]
  );

  useEffect(() => {
    const customerId = selectedId;
    if (!customerId || !hasProcessingMedia) return;

    let cancelled = false;
    let pollTimer: number | undefined;
    const pollController = new AbortController();
    const pollConversation = async () => {
      let shouldContinue = true;
      try {
        const { data } = await ApiService.get<{ messages: Message[] }>({
          url: buildConversationTimelineUrl(customerId),
          options: { signal: pollController.signal },
        });
        if (cancelled) return;
        if (Array.isArray(data?.messages)) {
          const refreshedMessages = data.messages;
          shouldContinue = hasProcessingInboxAttachments(refreshedMessages);
          updateConversationCaches((current) =>
            current.map((conversation) => {
              if (conversation.customerId !== customerId) return conversation;
              const mergedMessages = mergeInboxMessageTimelines(
                conversation.messages,
                refreshedMessages
              );
              shouldContinue =
                shouldContinue || hasProcessingInboxAttachments(mergedMessages);
              return { ...conversation, messages: mergedMessages };
            })
          );
        }
      } catch {
        // Archiving is asynchronous; keep polling after transient failures.
      }

      if (!cancelled && shouldContinue) {
        pollTimer = window.setTimeout(pollConversation, 2_000);
      }
    };

    pollTimer = window.setTimeout(pollConversation, 2_000);
    return () => {
      cancelled = true;
      pollController.abort();
      if (pollTimer !== undefined) window.clearTimeout(pollTimer);
    };
  }, [hasProcessingMedia, selectedId, updateConversationCaches]);

  const markRead = useCallback(
    (customerId: string) => {
      const delta =
        conversations.find((c) => c.customerId === customerId)?.unreadCount ??
        0;
      updateConversationCaches((prev) =>
        prev.map((c) => {
          if (c.customerId !== customerId) return c;
          return { ...c, unreadCount: 0 };
        })
      );
      window.dispatchEvent(
        new CustomEvent("inbox:marked-read", {
          detail: { customerId, unreadDelta: delta },
        })
      );
      persistRead(customerId);
    },
    [conversations, persistRead, updateConversationCaches]
  );

  useEffect(() => {
    const onMarkedAllRead = () => {
      updateConversationCaches((prev) =>
        prev.map((c) => ({ ...c, unreadCount: 0 }))
      );
      void queryClient.invalidateQueries({ queryKey: INBOX_KEY, exact: false });
    };
    window.addEventListener("inbox:marked-all-read", onMarkedAllRead);
    return () =>
      window.removeEventListener("inbox:marked-all-read", onMarkedAllRead);
  }, [queryClient, updateConversationCaches]);

  useEffect(() => {
    if (!selectedId) return;
    const c = conversations.find((x) => x.customerId === selectedId);
    if (c && c.unreadCount > 0) markRead(selectedId);
  }, [selectedId, conversations, markRead]);

  // SSE
  useEffect(() => {
    let cancelled = false;
    let ctrl: AbortController | null = null;
    let backoff = 1_000;
    const connect = async () => {
      if (cancelled) return;
      const token = TokenService.getLocalAccessToken();
      if (!token) return;
      ctrl = new AbortController();
      try {
        const resp = await fetch(`${baseUrl}/ai-builder/notifications/stream`, {
          signal: ctrl.signal,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
          },
        });
        if (!resp.ok || !resp.body) throw new Error();
        backoff = 1_000;
        const reader = resp.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const { frames, rest } = parseSseFrames(buf);
          buf = rest;
          for (const f of frames) {
            if (
              f.event === "inbox_update" ||
              f.event === "inbox_handoff_changed" ||
              f.event === "inbox_label_changed" ||
              f.event === "inbox_marked_read" ||
              f.event === "inbox_marked_all_read"
            )
              void queryClient.invalidateQueries({
                queryKey: INBOX_KEY,
                exact: false,
              });
          }
        }
      } catch (e: any) {
        if (cancelled || e?.name === "AbortError") return;
      }
      if (cancelled) return;
      await new Promise((r) => setTimeout(r, backoff));
      backoff = Math.min(backoff * 2, 30_000);
      void connect();
    };
    void connect();
    return () => {
      cancelled = true;
      ctrl?.abort();
    };
  }, [baseUrl, queryClient]);

  useEffect(() => {
    if (highlightedMessageId) return;
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [selectedId, messages.length, highlightedMessageId]);

  useEffect(() => {
    if (!selected || !highlightedMessageId) return;
    if (messages.some((m) => m.id === highlightedMessageId)) return;

    const loadKey = `${selected.customerId}:${highlightedMessageId}`;
    if (requestedMessageLoads.current[loadKey]) return;
    requestedMessageLoads.current[loadKey] = true;

    let cancelled = false;
    void ApiService.get<{ messages: Message[] }>({
      url: buildConversationTimelineUrl(
        selected.customerId,
        highlightedMessageId
      ),
    })
      .then(({ data }) => {
        if (cancelled) return;
        const loadedMessages = data?.messages ?? [];
        if (!loadedMessages.some((m) => m.id === highlightedMessageId)) {
          setHighlightedMessageId(null);
        }
        updateConversationCaches((cur) =>
          cur.map((c) =>
            c.customerId === selected.customerId
              ? { ...c, messages: loadedMessages }
              : c
          )
        );
      })
      .catch(() => {
        setHighlightedMessageId(null);
      });

    return () => {
      cancelled = true;
    };
  }, [highlightedMessageId, messages, selected, updateConversationCaches]);

  useEffect(() => {
    if (!highlightedMessageId) return;
    const el = messageRefs.current[highlightedMessageId];
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = window.setTimeout(() => {
      setHighlightedMessageId(null);
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [highlightedMessageId, messages.length, selectedId]);

  const selectReplyAttachments = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length === 0) return;

    const invalidFiles = files
      .map((file) => ({ file, error: inboxAttachmentValidationError(file) }))
      .filter(
        (result): result is { file: File; error: string } => !!result.error
      );
    const supported = files.filter(
      (file) => !inboxAttachmentValidationError(file)
    );
    const availableSlots = Math.max(
      0,
      MAX_INBOX_ATTACHMENTS - replyAttachments.length
    );
    const selectedFiles = supported.slice(0, availableSlots);
    if (selectedFiles.length > 0) {
      setReplyAttachments((current) => [
        ...current,
        ...selectedFiles.map((file) => ({
          key: `attachment-${Date.now()}-${attachmentKeySequence.current++}`,
          file,
        })),
      ]);
    }

    if (invalidFiles.length > 0) {
      setReplyError(invalidFiles[0].error);
    } else if (supported.length > availableSlots) {
      setReplyError(
        `You can attach up to ${MAX_INBOX_ATTACHMENTS} files to one message.`
      );
    } else {
      setReplyError(null);
    }
  };

  const removeReplyAttachment = (key: string) => {
    if (sending) return;
    setReplyAttachments((current) =>
      current.filter((attachment) => attachment.key !== key)
    );
    setReplyError(null);
  };

  const sendReply = async () => {
    const content = replyText.trim();
    if (!selected || sending || (!content && replyAttachments.length === 0)) {
      return;
    }

    setSending(true);
    setReplyError(null);
    try {
      const token = TokenService.getLocalAccessToken();
      const preparedAttachments = [...replyAttachments];
      for (let index = 0; index < preparedAttachments.length; index += 1) {
        const pending = preparedAttachments[index];
        if (pending.reference) continue;

        setSendStatus(
          `Uploading attachment ${index + 1} of ${preparedAttachments.length}…`
        );
        const uploaded = await uploadInboxAttachment({
          baseUrl,
          token,
          file: pending.file,
        });
        preparedAttachments[index] = {
          ...pending,
          reference: uploaded.reference,
        };
        setReplyAttachments((current) =>
          current.map((attachment) =>
            attachment.key === pending.key
              ? { ...attachment, reference: uploaded.reference }
              : attachment
          )
        );
      }

      setSendStatus("Sending…");
      const attachmentReferences = preparedAttachments.flatMap((attachment) =>
        attachment.reference ? [attachment.reference] : ([] as string[])
      );
      const options: { content?: string; attachments?: string[] } = {};
      if (content) options.content = content;
      if (attachmentReferences.length > 0) {
        options.attachments = attachmentReferences;
      }
      const { data } = await ApiService.post<{ message: Message }>({
        url: `/inbox/conversations/${encodeURIComponent(
          selected.customerId
        )}/reply`,
        options,
      });
      const msg = data.message;
      updateConversationCaches((cur) =>
        cur.map((c) =>
          c.customerId === selected.customerId
            ? {
                ...c,
                channels: c.channels.includes("sms")
                  ? c.channels
                  : [...c.channels, "sms"],
                lastMessage: {
                  direction: msg.direction,
                  authorType: msg.authorType,
                  channel: msg.channel,
                  content: msg.content,
                  createdAt: msg.createdAt,
                },
                messages: [...c.messages, msg],
              }
            : c
        )
      );
      setReplyText("");
      setReplyAttachments([]);
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
    } catch (e: any) {
      setReplyError(
        e?.response?.data?.message || e?.message || "SMS reply failed"
      );
    } finally {
      setSendStatus(null);
      setSending(false);
    }
  };

  const saveAdminTrainingNote = async () => {
    if (!selected || !adminNoteText.trim() || savingAdminNote) return;
    const sourceMessage = latestMessage;
    setSavingAdminNote(true);
    setAdminNoteError(null);
    try {
      const { data } = await ApiService.post<{ note: AdminTrainingNote }>({
        url: `/inbox/conversations/${encodeURIComponent(
          selected.customerId
        )}/admin-training-notes`,
        options: {
          content: adminNoteText.trim(),
          messageId: latestMessage?.id ?? null,
        },
      });
      const note = data.note;
      queryClient.setQueryData<AdminTrainingNote[]>(adminNotesKey, (cur) => [
        note,
        ...(cur ?? []),
      ]);
      setAdminNoteText("");
      const message = buildAdminTrainingAssistantMessage({
        note,
        selected,
        messages,
        sourceMessage,
      });
      window.dispatchEvent(
        new CustomEvent("leadstats:admin-training-note-created", {
          detail: {
            note,
            customerId: selected.customerId,
            workflowId: note.workflowId ?? selected.lastWorkflowId,
            stepId: note.stepId ?? sourceMessage?.stepId ?? selected.lastStepId,
            message,
            screenContext: formatAdminTrainingConversationContext(
              selected,
              messages
            ),
          },
        })
      );
      showToast("Admin note saved. AI assistant is reviewing it.");
    } catch (e: any) {
      setAdminNoteError(
        e?.response?.data?.message || e?.message || "Admin note failed"
      );
    } finally {
      setSavingAdminNote(false);
    }
  };

  const setLabel = async (
    customerId: string,
    label: "flagged" | "resolved" | null
  ) => {
    setLabelLoading(true);
    try {
      await ApiService.post({
        url: `/inbox/conversations/${encodeURIComponent(customerId)}/label`,
        options: { label },
      });
      updateConversationCaches((cur) =>
        cur.map((c) => (c.customerId === customerId ? { ...c, label } : c))
      );
    } catch {
      showToast("Failed to update conversation status.");
    } finally {
      setLabelLoading(false);
    }
  };

  const resolveHandoff = async (customerId: string) => {
    setLabelLoading(true);
    try {
      await ApiService.post({
        url: `/inbox/conversations/${encodeURIComponent(
          customerId
        )}/handoff/resolve`,
        options: {},
      });
      updateConversationCaches((cur) =>
        cur.map((c) =>
          c.customerId === customerId
            ? {
                ...c,
                needsHuman: false,
                handoffReasons: [],
                handoffSummary: null,
                handoffSourceMessageId: null,
                handoffUpdatedAt: null,
              }
            : c
        )
      );
      void queryClient.invalidateQueries({ queryKey: INBOX_KEY, exact: false });
    } catch {
      showToast("Failed to resolve handoff.");
    } finally {
      setLabelLoading(false);
    }
  };

  const saveSettings = async () => {
    const days = parseInt(overdueDaysInput, 10);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      showToast("Enter a number between 1 and 365.");
      return;
    }
    try {
      await ApiService.patch({
        url: "/inbox/settings",
        options: { overdueDays: days },
      });
      queryClient.setQueryData<InboxSettings>(SETTINGS_KEY, {
        overdueDays: days,
      });
      setShowSettings(false);
    } catch {
      showToast("Failed to save settings.");
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="relative flex flex-col h-full bg-themed-background overflow-hidden font-inter text-[14px] text-themed-text">
      {/* Toast */}
      {toast && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-50 bg-[#1F2937] text-white text-[13px] rounded-lg px-4 py-2 shadow-xl pointer-events-none">
          {toast}
        </div>
      )}

      {/* ── Header row ──────────────────────────────────────────────────── */}
      <div className="shrink-0 flex items-center justify-between px-6 py-3 border-b border-themed-border bg-themed-card">
        <div className="flex items-center gap-2.5">
          <span className="text-[17px] font-semibold tracking-[-0.01em]">
            Messages
          </span>
          {totalUnread > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#6366F1] text-white text-[11px] font-bold leading-none">
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowSettings((v) => !v)}
          className={`flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md border transition-colors ${
            showSettings
              ? "border-[#6366F1] bg-[#6366F1]/[0.07] text-[#6366F1]"
              : "border-themed-border text-themed-textSecondary hover:text-themed-text hover:bg-themed-hover"
          }`}
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          Settings
        </button>
      </div>

      {/* ── Settings panel ──────────────────────────────────────────────── */}
      {showSettings && (
        <div className="shrink-0 flex items-center gap-3 px-6 py-2.5 border-b border-themed-border bg-themed-card/60 text-[13px]">
          <span className="text-themed-textSecondary">Overdue after</span>
          <input
            type="number"
            min={1}
            max={365}
            value={overdueDaysInput}
            onChange={(e) => setOverdueDaysInput(e.target.value)}
            className="w-16 rounded border border-themed-border bg-themed-background px-2.5 py-1 text-[13px] outline-none focus:ring-2 focus:ring-[#6366F1]/25 focus:border-[#6366F1]"
          />
          <span className="text-themed-textSecondary">
            days without a reply
          </span>
          <button
            onClick={saveSettings}
            className="rounded bg-[#6366F1] px-3 py-1 text-[12px] font-semibold text-white hover:bg-[#4F46E5] transition-colors"
          >
            Save
          </button>
          <button
            onClick={() => setShowSettings(false)}
            className="text-themed-textSecondary hover:text-themed-text text-[12px] transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Primary tabs ────────────────────────────────────────────────── */}
      <div
        ref={tabsRef}
        className="shrink-0 flex items-center gap-0.5 px-4 pt-2 border-b border-themed-border bg-themed-card overflow-x-auto scrollbar-none"
        style={{ scrollbarWidth: "none" }}
      >
        {TABS.map((tab) => {
          const active = category === tab.key;
          const unreadForTab = tab.key === "unread" ? totalUnread : 0;
          return (
            <button
              key={tab.key}
              onClick={() => setCategory(tab.key)}
              className={`shrink-0 relative flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium whitespace-nowrap transition-colors ${
                active
                  ? "text-[#6366F1]"
                  : "text-themed-textSecondary hover:text-themed-text"
              }`}
            >
              {tab.label}
              {unreadForTab > 0 && (
                <span
                  className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-none ${
                    active
                      ? "bg-[#6366F1] text-white"
                      : "bg-themed-border text-themed-textSecondary"
                  }`}
                >
                  {unreadForTab > 99 ? "99+" : unreadForTab}
                </span>
              )}
              {active && (
                <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#6366F1] rounded-t-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* ── Secondary filters ───────────────────────────────────────────── */}
      <div className="shrink-0 flex items-center gap-4 px-5 py-2 border-b border-themed-border bg-themed-background overflow-x-auto">
        {/* Direction */}
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold text-themed-text uppercase tracking-wide">
            Direction
          </span>
          <div className="flex items-center gap-1">
            {(["all", "inbound", "outbound"] as const).map((d) => {
              const active = directionFilter === d;
              return (
                <button
                  key={d}
                  onClick={() => setDirectionFilter(d)}
                  className={`px-2.5 py-0.5 text-[12px] rounded-full font-medium transition-colors capitalize ${
                    active
                      ? "bg-[#6366F1] text-white"
                      : "text-themed-textSecondary hover:bg-themed-hover hover:text-themed-text"
                  }`}
                >
                  {d === "all" ? "All" : d}
                </button>
              );
            })}
          </div>
        </div>

        <div className="w-px h-4 bg-[#6B7280]" />

        {/* Channel */}
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold text-themed-text uppercase tracking-wide">
            Channel
          </span>
          <div className="flex items-center gap-1 flex-wrap">
            {(
              [
                { key: "all", label: "All" },
                { key: "sms", label: "SMS" },
                { key: "email", label: "Email" },
                { key: "slack", label: "Slack" },
                { key: "push", label: "Push" },
                { key: "webhook", label: "Webhook" },
                { key: "in-app", label: "In-App" },
              ] as const
            ).map(({ key, label }) => {
              const active = channelFilter === key;
              return (
                <button
                  key={key}
                  onClick={() => setChannelFilter(key)}
                  className={`px-2.5 py-0.5 text-[12px] rounded-full font-medium transition-colors ${
                    active
                      ? "bg-[#6366F1] text-white"
                      : "text-themed-textSecondary hover:bg-themed-hover hover:text-themed-text"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="w-px h-4 bg-[#6B7280]" />

        {/* Agency */}
        <div className="flex items-center gap-1.5 shrink-0">
          <label
            htmlFor="inbox-agency-filter"
            className="text-[11px] font-semibold text-themed-text uppercase tracking-wide"
          >
            Agency
          </label>
          <select
            id="inbox-agency-filter"
            value={agencyFilter}
            onChange={(e) => setAgencyFilter(e.target.value)}
            className="max-w-[240px] rounded border border-themed-border bg-themed-background px-2 py-0.5 text-[12px] text-themed-text outline-none focus:ring-2 focus:ring-[#6366F1]/25 focus:border-[#6366F1] cursor-pointer"
          >
            <option value="all">All agencies</option>
            {agencies.map((agency) => (
              <option key={agency.id} value={agency.id}>
                {agencyOptionLabel(agency)}
              </option>
            ))}
          </select>
        </div>

        {workflowOptions.length > 0 && (
          <>
            <div className="w-px h-4 bg-[#6B7280]" />

            {/* Workflow */}
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-semibold text-themed-text uppercase tracking-wide">
                Workflow
              </span>
              <select
                value={workflowFilter}
                onChange={(e) => setWorkflowFilter(e.target.value)}
                className="rounded border border-themed-border bg-themed-background px-2 py-0.5 text-[12px] text-themed-text outline-none focus:ring-2 focus:ring-[#6366F1]/25 focus:border-[#6366F1] cursor-pointer"
              >
                <option value="all">All</option>
                {workflowOptions.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {/* ── Error banner ────────────────────────────────────────────────── */}
      {loadError && (
        <div className="shrink-0 mx-5 mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-[13px] text-red-700">
          {loadError}
        </div>
      )}

      {/* ── Two-panel body ──────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 flex">
        {/* Conversation list */}
        <div className="w-[320px] shrink-0 border-r border-themed-border flex flex-col">
          <div className="shrink-0 px-4 py-2 border-b border-themed-border flex items-center justify-between">
            <span className="text-[12px] text-themed-textSecondary">
              {filtered.length} conversation{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isPending ? (
              <div className="flex flex-col gap-2 p-4">
                {[1, 2, 3, 4].map((n) => (
                  <div
                    key={n}
                    className="rounded-lg bg-themed-border/30 h-16 animate-pulse"
                  />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2 text-themed-textSecondary">
                <svg
                  className="w-8 h-8 opacity-30"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                <p className="text-[13px]">No conversations</p>
              </div>
            ) : (
              filtered.map((c) => {
                const active = c.customerId === selectedId;
                const over = isOverdue(c, settings.overdueDays ?? 7);
                return (
                  <button
                    key={c.customerId}
                    onClick={() => setSelectedId(c.customerId)}
                    className={`w-full text-left px-4 py-3.5 border-b border-themed-border/40 transition-colors ${
                      active
                        ? "bg-[#6366F1]/[0.07] border-l-[3px] border-l-[#6366F1]"
                        : "hover:bg-themed-hover border-l-[3px] border-l-transparent"
                    }`}
                  >
                    {/* Name + workflow + time */}
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span
                          className={`font-medium text-[13px] truncate ${
                            c.unreadCount > 0
                              ? "text-themed-text"
                              : "text-themed-textSecondary"
                          }`}
                        >
                          {c.customerName ||
                            c.customerPhone ||
                            `Contact ${c.customerId}`}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {c.unreadCount > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#6366F1] text-white text-[10px] font-bold">
                            {c.unreadCount > 99 ? "99+" : c.unreadCount}
                          </span>
                        )}
                        {c.lastMessage && (
                          <span className="text-[11px] text-themed-textSecondary whitespace-nowrap">
                            {shortTime(c.lastMessage.createdAt)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    {c.customerEmail && (
                      <p className="text-[11px] text-themed-textSecondary truncate mb-0.5">
                        {c.customerEmail}
                      </p>
                    )}

                    {/* Preview */}
                    {c.lastMessage?.content && (
                      <p className="text-[12px] text-themed-textSecondary truncate mb-1.5">
                        {c.lastMessage.direction === "outbound" && (
                          <span className="font-medium">You: </span>
                        )}
                        {c.lastMessage.content.slice(0, 80)}
                      </p>
                    )}

                    {/* Badges row */}
                    <div className="flex items-center gap-1 flex-wrap">
                      {c.channels.slice(0, 3).map((ch) => (
                        <span
                          key={ch}
                          className={`text-[10px] font-medium rounded px-1.5 py-0 leading-[1.6] ${channelChipClass(
                            ch
                          )}`}
                        >
                          {channelLabel(ch)}
                        </span>
                      ))}
                      {conversationAgencyLabel(c) && (
                        <span
                          title={
                            c.agencyCode
                              ? `Agency code ${c.agencyCode}`
                              : undefined
                          }
                          className="text-[10px] font-medium rounded px-1.5 py-0 leading-[1.6] bg-sky-50 text-sky-700"
                        >
                          {conversationAgencyLabel(c)}
                        </span>
                      )}
                      {c.label === "resolved" && (
                        <span className="text-[10px] font-medium rounded px-1.5 py-0 leading-[1.6] bg-emerald-50 text-emerald-700">
                          Resolved
                        </span>
                      )}
                      {c.label === "flagged" && (
                        <span className="text-[10px] font-medium rounded px-1.5 py-0 leading-[1.6] bg-amber-50 text-amber-700">
                          Flagged
                        </span>
                      )}
                      {c.needsHuman && (
                        <span className="text-[10px] font-medium rounded px-1.5 py-0 leading-[1.6] bg-[#111827] text-white">
                          Needs Human
                        </span>
                      )}
                      {c.handoffReasons.slice(0, 2).map((reason) => (
                        <span
                          key={reason}
                          className="text-[10px] font-medium rounded px-1.5 py-0 leading-[1.6] bg-[#374151] text-[#F9FAFB]"
                        >
                          {handoffReasonLabel(reason)}
                        </span>
                      ))}
                      {over && !c.label && (
                        <span className="text-[10px] font-medium rounded px-1.5 py-0 leading-[1.6] bg-red-50 text-red-600">
                          Overdue
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 flex flex-col min-w-0">
          {selected ? (
            <>
              {/* Conversation header */}
              <div className="shrink-0 px-5 py-3 border-b border-themed-border bg-themed-card flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[15px] truncate">
                      {contactLabel(selected)}
                    </span>
                    {selected.hasWorkflowMessages &&
                      selected.lastWorkflowName &&
                      (selected.lastWorkflowId ? (
                        <button
                          onClick={() =>
                            navigate(`/flow/${selected.lastWorkflowId}/view`, {
                              state: {
                                fromMessage: {
                                  customerId: selected.customerId,
                                  contactName: contactLabel(selected),
                                },
                              },
                            })
                          }
                          className="shrink-0 text-[11px] font-medium rounded px-1.5 leading-[1.6] bg-violet-50 text-violet-700 border border-violet-100 hover:bg-violet-100 transition-colors cursor-pointer"
                        >
                          {selected.lastWorkflowName} ↗
                        </button>
                      ) : (
                        <span className="shrink-0 text-[11px] font-medium rounded px-1.5 leading-[1.6] bg-violet-50 text-violet-700 border border-violet-100">
                          {selected.lastWorkflowName}
                        </span>
                      ))}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {selected.customerEmail && (
                      <span className="text-[12px] text-themed-textSecondary">
                        {selected.customerEmail}
                      </span>
                    )}
                    {selected.customerPhone && (
                      <span className="text-[12px] text-themed-textSecondary">
                        {selected.customerPhone}
                      </span>
                    )}
                    {conversationAgencyLabel(selected) && (
                      <span
                        title={
                          selected.agencyCode
                            ? `Agency code ${selected.agencyCode}`
                            : undefined
                        }
                        className="text-[11px] font-medium rounded px-1.5 leading-[1.6] bg-sky-50 text-sky-700 border border-sky-100"
                      >
                        {conversationAgencyLabel(selected)}
                      </span>
                    )}
                    {selected.label === "resolved" && (
                      <span className="text-[11px] font-medium rounded px-1.5 leading-[1.6] bg-emerald-50 text-emerald-700 border border-emerald-100">
                        Resolved
                      </span>
                    )}
                    {selected.label === "flagged" && (
                      <span className="text-[11px] font-medium rounded px-1.5 leading-[1.6] bg-amber-50 text-amber-700 border border-amber-100">
                        Flagged
                      </span>
                    )}
                    {selected.needsHuman && (
                      <span className="text-[11px] font-medium rounded px-1.5 leading-[1.6] bg-[#111827] text-white border border-[#111827]">
                        Needs Human
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {selected.label !== "resolved" ? (
                    <button
                      disabled={labelLoading}
                      onClick={() => setLabel(selected.customerId, "resolved")}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Resolve
                    </button>
                  ) : (
                    <button
                      disabled={labelLoading}
                      onClick={() => setLabel(selected.customerId, null)}
                      className="px-3 py-1.5 text-[12px] font-medium rounded-md border border-themed-border text-themed-textSecondary hover:bg-themed-hover disabled:opacity-50 transition-colors"
                    >
                      Reopen
                    </button>
                  )}

                  {selected.label !== "flagged" ? (
                    <button
                      disabled={labelLoading}
                      onClick={() => setLabel(selected.customerId, "flagged")}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-md border border-amber-200 text-amber-700 hover:bg-amber-50 disabled:opacity-50 transition-colors"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 21V4m0 0l7-1 4 1 7-1v13l-7 1-4-1-7 1"
                        />
                      </svg>
                      Flag
                    </button>
                  ) : (
                    <button
                      disabled={labelLoading}
                      onClick={() => setLabel(selected.customerId, null)}
                      className="px-3 py-1.5 text-[12px] font-medium rounded-md border border-themed-border text-themed-textSecondary hover:bg-themed-hover disabled:opacity-50 transition-colors"
                    >
                      Unflag
                    </button>
                  )}
                  {selected.needsHuman && (
                    <button
                      disabled={labelLoading}
                      onClick={() => resolveHandoff(selected.customerId)}
                      className="px-3 py-1.5 text-[12px] font-medium rounded-md border border-[#374151] bg-[#111827] text-white hover:bg-[#1F2937] disabled:opacity-50 transition-colors"
                    >
                      Resolve handoff
                    </button>
                  )}
                </div>
              </div>

              {selected.needsHuman && (
                <div className="shrink-0 border-b border-[#374151] bg-[#111827] px-5 py-3 text-white">
                  <div className="flex flex-wrap items-center gap-2 text-[12px]">
                    <span className="font-semibold">
                      Human follow-up needed
                    </span>
                    {selected.handoffReasons.map((reason) => (
                      <span
                        key={reason}
                        className="rounded border border-white/20 bg-white/10 px-1.5 leading-[1.6] text-[10px] font-medium"
                      >
                        {handoffReasonLabel(reason)}
                      </span>
                    ))}
                  </div>
                  {selected.handoffSummary && (
                    <p className="mt-1 text-[12px] leading-relaxed text-white/80">
                      {selected.handoffSummary}
                    </p>
                  )}
                </div>
              )}

              {/* Messages */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-6 py-5 space-y-5"
              >
                {messages.length === 0 ? (
                  <p className="text-[13px] text-themed-textSecondary text-center mt-10">
                    No messages yet.
                  </p>
                ) : (
                  messages.map((m) => {
                    const inbound = m.direction === "inbound";
                    const highlighted = highlightedMessageId === m.id;
                    const attachments = getInboxMessageAttachments(
                      m.traceMetadata
                    );
                    return (
                      <div
                        key={m.id}
                        ref={(node) => {
                          messageRefs.current[m.id] = node;
                        }}
                        className={`flex flex-col ${
                          inbound ? "items-start" : "items-end"
                        }`}
                      >
                        <div
                          className={`flex items-center gap-2 mb-1.5 text-[11px] text-themed-textSecondary ${
                            inbound ? "" : "flex-row-reverse"
                          }`}
                        >
                          <span
                            className={`font-medium rounded px-1.5 leading-[1.6] text-[10px] ${
                              m.authorType === "customer"
                                ? "bg-blue-50 text-blue-700"
                                : m.authorType === "ai"
                                ? "bg-violet-50 text-violet-700"
                                : m.authorType === "operator"
                                ? "bg-sky-50 text-sky-700"
                                : m.authorType === "workflow"
                                ? "bg-violet-50 text-violet-700"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {m.authorType === "customer"
                              ? "Contact"
                              : m.authorType === "operator"
                              ? "Agent"
                              : m.authorType === "workflow"
                              ? "Workflow"
                              : m.authorType === "ai"
                              ? "AI"
                              : "System"}
                          </span>
                          <span
                            className={`text-[10px] font-medium rounded px-1.5 leading-[1.6] ${channelChipClass(
                              m.channel
                            )}`}
                          >
                            {channelLabel(m.channel)}
                          </span>
                          {m.workflowId && (
                            <span className="bg-violet-50 text-violet-700 text-[10px] font-medium rounded px-1.5 leading-[1.6]">
                              Via Workflow
                            </span>
                          )}
                          <span>{longTime(m.createdAt)}</span>
                        </div>
                        <div
                          className={`max-w-[560px] rounded-2xl px-4 py-2.5 text-[13px] whitespace-pre-wrap break-words leading-relaxed transition-shadow ${
                            inbound
                              ? "bg-themed-card border border-themed-border rounded-tl-sm"
                              : m.authorType === "ai"
                              ? "bg-violet-50 border border-violet-200 text-violet-900 rounded-tr-sm"
                              : m.authorType === "workflow"
                              ? "bg-[#6366F1]/[0.07] border border-[#6366F1]/20 rounded-tr-sm"
                              : "bg-[#6366F1] text-white rounded-tr-sm"
                          } ${highlighted ? "ring-2 ring-[#EF4444]/50" : ""}`}
                        >
                          {m.content ? (
                            <div>{m.content}</div>
                          ) : attachments.length === 0 ? (
                            <span className="italic opacity-40">
                              (no content)
                            </span>
                          ) : null}
                          <InboxMessageAttachments
                            attachments={attachments}
                            baseUrl={baseUrl}
                            messageId={m.id}
                            outbound={!inbound}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Reply and admin notes */}
              <div className="shrink-0 border-t border-themed-border bg-themed-card px-5 py-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="min-w-0">
                    {canSms ? (
                      <div className="flex flex-col gap-2.5">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          disabled={sending}
                          rows={3}
                          maxLength={1600}
                          placeholder="Reply by SMS..."
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                              e.preventDefault();
                              void sendReply();
                            }
                          }}
                          className="w-full resize-none rounded-xl border border-themed-border bg-themed-background px-3.5 py-2.5 text-[13px] outline-none focus:ring-2 focus:ring-[#6366F1]/25 focus:border-[#6366F1] disabled:opacity-60 transition-shadow"
                        />
                        {replyAttachments.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {replyAttachments.map((attachment) => (
                              <span
                                key={attachment.key}
                                className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-themed-border bg-themed-background px-2 py-1 text-[11px] text-themed-text"
                                title={attachment.file.name}
                              >
                                <svg
                                  className="h-3.5 w-3.5 shrink-0 text-[#6366F1]"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                  aria-hidden="true"
                                >
                                  <path d="m20 11.5-8.5 8.5a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8.5-8.5" />
                                </svg>
                                <span className="max-w-[170px] truncate font-medium">
                                  {attachment.file.name}
                                </span>
                                <span className="shrink-0 text-themed-textSecondary">
                                  {formatInboxAttachmentSize(
                                    attachment.file.size
                                  )}
                                </span>
                                {attachment.reference && (
                                  <span
                                    className="text-emerald-600"
                                    title="Uploaded"
                                    aria-label="Uploaded"
                                  >
                                    ✓
                                  </span>
                                )}
                                <button
                                  type="button"
                                  disabled={sending}
                                  onClick={() =>
                                    removeReplyAttachment(attachment.key)
                                  }
                                  className="ml-0.5 rounded p-0.5 text-themed-textSecondary hover:bg-themed-hover hover:text-themed-text disabled:cursor-not-allowed disabled:opacity-50"
                                  aria-label={`Remove ${attachment.file.name}`}
                                >
                                  <svg
                                    className="h-3.5 w-3.5"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    aria-hidden="true"
                                  >
                                    <path d="m6 6 12 12M18 6 6 18" />
                                  </svg>
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex min-w-0 items-center gap-2">
                            <input
                              ref={attachmentInputRef}
                              type="file"
                              multiple
                              accept={INBOX_ATTACHMENT_ACCEPT}
                              disabled={
                                sending ||
                                replyAttachments.length >= MAX_INBOX_ATTACHMENTS
                              }
                              onChange={selectReplyAttachments}
                              className="hidden"
                              aria-label="Choose message attachments"
                            />
                            <button
                              type="button"
                              disabled={
                                sending ||
                                replyAttachments.length >= MAX_INBOX_ATTACHMENTS
                              }
                              onClick={() =>
                                attachmentInputRef.current?.click()
                              }
                              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-themed-border px-2 py-1 text-[11px] font-medium text-themed-textSecondary hover:bg-themed-hover hover:text-themed-text disabled:cursor-not-allowed disabled:opacity-40"
                              aria-label="Attach files"
                            >
                              <svg
                                className="h-3.5 w-3.5"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2}
                                aria-hidden="true"
                              >
                                <path d="m20 11.5-8.5 8.5a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8.5-8.5" />
                              </svg>
                              Attach
                            </button>
                            <span className="min-w-0 text-[11px]">
                              {replyError ? (
                                <span className="break-words text-red-500">
                                  {replyError}
                                </span>
                              ) : sendStatus ? (
                                <span className="text-themed-textSecondary">
                                  {sendStatus}
                                </span>
                              ) : (
                                <span className="text-themed-textSecondary">
                                  {replyText.length}/1600 ·{" "}
                                  {replyAttachments.length}/
                                  {MAX_INBOX_ATTACHMENTS} files
                                </span>
                              )}
                            </span>
                          </div>
                          <button
                            disabled={
                              (!replyText.trim() &&
                                replyAttachments.length === 0) ||
                              sending
                            }
                            onClick={sendReply}
                            className="ml-3 shrink-0 rounded-lg bg-[#6366F1] px-4 py-1.5 text-[13px] font-semibold text-white hover:bg-[#4F46E5] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            {sending
                              ? "Sending…"
                              : replyAttachments.length > 0
                              ? "Send MMS"
                              : "Send SMS"}
                          </button>
                        </div>
                        <p className="text-[10px] text-themed-textSecondary">
                          JPEG/PNG up to 5 MB · PDF/vCard up to 500 KB · Ctrl/⌘
                          Enter to send
                        </p>
                      </div>
                    ) : (
                      <p className="text-[13px] text-themed-textSecondary text-center py-0.5">
                        SMS replies only. No SMS on this conversation.
                      </p>
                    )}
                  </div>

                  <div className="min-w-0 rounded-xl border border-themed-border bg-themed-background p-3 text-themed-text">
                    <textarea
                      value={adminNoteText}
                      onChange={(e) => setAdminNoteText(e.target.value)}
                      disabled={savingAdminNote}
                      rows={3}
                      maxLength={1200}
                      placeholder="What should the AI have done differently?"
                      className="w-full resize-none rounded-lg border border-themed-border bg-themed-card px-3 py-2 text-[12px] text-themed-text placeholder:text-themed-textSecondary outline-none focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/25 disabled:opacity-60"
                    />
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-[11px] font-medium text-themed-textSecondary">
                        {adminNoteError ? (
                          <span className="text-red-400">{adminNoteError}</span>
                        ) : (
                          <span>
                            Admin notes to AI · {adminTrainingNotes.length}{" "}
                            notes · {adminNoteText.length}/1200
                          </span>
                        )}
                      </span>
                      <button
                        disabled={!adminNoteText.trim() || savingAdminNote}
                        onClick={saveAdminTrainingNote}
                        className="rounded-lg bg-[#6366F1] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#4F46E5] disabled:cursor-not-allowed disabled:bg-themed-border disabled:text-themed-textSecondary transition-colors"
                      >
                        {savingAdminNote ? "Saving..." : "Add note"}
                      </button>
                    </div>

                    {adminTrainingNotes.length > 0 && (
                      <div className="mt-3 max-h-[150px] space-y-2 overflow-y-auto pr-1">
                        {adminTrainingNotes.map((note) => (
                          <div
                            key={note.id}
                            className="rounded-lg border border-themed-border bg-themed-card p-2 text-themed-text"
                          >
                            <div className="mb-1.5 flex items-center justify-between gap-2">
                              <span
                                className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold ${adminTrainingStatusClass(
                                  note.status
                                )}`}
                              >
                                {note.status !== "pending" && (
                                  <svg
                                    className="h-3 w-3"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={3}
                                    aria-hidden="true"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                )}
                                {adminTrainingStatusLabel(note.status)}
                              </span>
                              <span className="text-[10px] font-medium text-themed-textSecondary">
                                {shortTime(note.createdAt)}
                              </span>
                            </div>
                            <div className="whitespace-pre-wrap break-words text-[12px] leading-relaxed text-themed-text">
                              {note.content}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-themed-textSecondary">
              <svg
                className="w-10 h-10 opacity-25"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <p className="text-[13px]">
                {isPending ? "Loading inbox…" : "Select a conversation"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inbox;
